// AppErrorBoundary — last line of defence so a render error never leaves a black screen.
// A stale-chunk error (tab running an old build after a deploy) reloads once to load the new build;
// anything else shows a short message with a Reload button.
// NOTE (FP exception): React still offers error boundaries only as class components — this is the one
// class in the UI and holds no business logic; the decisions live in app-recovery.ts.

import { Component } from 'react';
import type { ErrorInfo, JSX, ReactNode } from 'react';
import { isChunkLoadError, reloadForNewBuild } from '../app-recovery.ts';

type Props = Readonly<{ readonly children: ReactNode }>;
type State = Readonly<{ readonly failed: boolean }>;

export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    if (isChunkLoadError(error) && reloadForNewBuild()) return;
    console.error('[app] render error', error, info.componentStack);
  }

  override render(): JSX.Element | ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="app-error" role="alert">
        <h2>Something went wrong</h2>
        <p>The app may have just been updated.</p>
        <button type="button" className="flow-step-btn" onClick={() => window.location.reload()}>Reload</button>
      </div>
    );
  }
}
